import { useEffect, useState } from "react";
import { useNavigate } from "../router";
import { fetchTicketsWithPerson } from "../utils/fetchTicketsWithPerson";
import { formatChatListTime, toPersianDigits } from "../utils/persianDate";
import { getChatListPreviewPlainText } from "../utils/chatListPreview";
import { SettingsRow, SettingsSection } from "@/common/components/settings/settingsList";
import { LoadFailed, RowsSkeleton } from "@/modules/doctorHome/components/profileSettings/chatSettingsUi";

function getTicketTitle(ticket) {
  return ticket?.title?.trim() || `گفت‌وگو #${toPersianDigits(ticket.id)}`;
}

function getTicketPreview(ticket) {
  const raw = ticket?.last_message_content || ticket?.first_message_content || "";
  const text = getChatListPreviewPlainText(raw, 72);
  if (text) return text;
  if (ticket?.last_message_attachment) return "پیوست";
  return "بدون پیام";
}

export default function UserProfileChatsList({
  personRemoteId,
  currentTicketId = null,
  onClose,
  onTicketsLoaded,
}) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (personRemoteId == null) {
      setTickets([]);
      setLoading(false);
      onTicketsLoaded?.([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchTicketsWithPerson(personRemoteId)
      .then((list) => {
        if (!cancelled) {
          const next = Array.isArray(list) ? list : [];
          setTickets(next);
          onTicketsLoaded?.(next);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTickets([]);
          setFailed(true);
          onTicketsLoaded?.([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [personRemoteId, onTicketsLoaded, attempt]);

  const handleSelect = (ticketId) => {
    navigate(`/chats/${ticketId}`);
    onClose?.();
  };

  const hasTickets = tickets.length > 0;

  return (
    <section className="flex flex-col gap-2" aria-label="گفت‌وگوهای مشترک">
      {failed ? (
        <LoadFailed onRetry={() => setAttempt((n) => n + 1)} />
      ) : loading ? (
        <RowsSkeleton rows={3} />
      ) : !hasTickets ? (
        <SettingsSection>
          <p className="m-0 px-[18px] py-4 text-center text-[14px] leading-6 text-[#767c86]">گفت‌وگوی مشترکی ندارید</p>
        </SettingsSection>
      ) : (
        <SettingsSection>
          {tickets.map((ticket) => {
            const isActive =
              currentTicketId != null && Number(currentTicketId) === Number(ticket.id);
            const timeLabel = formatChatListTime(ticket.updated_at || ticket.created_at);
            return (
              <SettingsRow
                key={ticket.id}
                title={getTicketTitle(ticket)}
                subtitle={getTicketPreview(ticket)}
                value={isActive ? "همین گفت‌وگو" : timeLabel || undefined}
                onClick={() => handleSelect(ticket.id)}
              />
            );
          })}
        </SettingsSection>
      )}
    </section>
  );
}
